import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiMessageCircle, FiX } = FiIcons

const FloatingAvatar = ({ 
  character = '🤖', 
  position = 'bottom-right',
  messages = [],
  isVisible = true,
  onMessageClick,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [currentMessage, setCurrentMessage] = useState('')
  const [messageIndex, setMessageIndex] = useState(0)

  const positions = {
    'bottom-right': 'bottom-6 right-6',
    'bottom-left': 'bottom-6 left-6',
    'top-right': 'top-6 right-6',
    'top-left': 'top-6 left-6'
  }

  useEffect(() => {
    if (messages.length > 0) {
      setCurrentMessage(messages[messageIndex])
      
      // Auto-cycle through messages
      const interval = setInterval(() => {
        setMessageIndex((prev) => (prev + 1) % messages.length)
      }, 4000)

      return () => clearInterval(interval)
    }
  }, [messages, messageIndex])

  const defaultMessages = [
    "Ready for your next task? 🌟",
    "You're doing great! Keep it up! 💪",
    "Don't forget to check your rewards! 🎁",
    "How about completing a chore? ⭐",
    "Family teamwork makes the dream work! 👨‍👩‍👧‍👦"
  ]

  const messagesToShow = messages.length > 0 ? messages : defaultMessages

  return (
    <AnimatePresence>
      {isVisible && (
        <div className={`fixed ${positions[position]} z-40 ${className}`}>
          {/* Message Bubble */}
          <AnimatePresence>
            {isExpanded && currentMessage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 10 }}
                className="absolute bottom-20 right-0 mb-2 p-4 bg-white rounded-2xl shadow-xl border-2 border-primary-200 max-w-xs"
                onClick={() => onMessageClick && onMessageClick(currentMessage)}
              >
                <div className="text-sm text-gray-800 font-medium">
                  {currentMessage}
                </div>
                
                {/* Close button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsExpanded(false)
                  }}
                  className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                >
                  <SafeIcon icon={FiX} className="text-xs" />
                </button>

                {/* Speech bubble arrow */}
                <div className="absolute bottom-0 right-6 transform translate-y-1/2 rotate-45 w-3 h-3 bg-white border-r-2 border-b-2 border-primary-200" />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Avatar */}
          <motion.div
            className="relative cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            animate={{
              y: [0, -10, 0],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary-400/30 to-secondary-400/30 rounded-full blur-lg scale-110" />
            
            {/* Avatar container */}
            <div className="relative w-16 h-16 bg-gradient-to-br from-primary-100 via-white to-secondary-100 rounded-full shadow-xl border-3 border-white flex items-center justify-center">
              <span className="text-3xl">{character}</span>
              
              {/* Notification dot */}
              {!isExpanded && messagesToShow.length > 0 && (
                <motion.div
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center"
                  animate={{
                    scale: [1, 1.2, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <SafeIcon icon={FiMessageCircle} className="text-white text-xs" />
                </motion.div>
              )}
            </div>

            {/* Floating particles */}
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(4)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-yellow-400 rounded-full"
                  style={{
                    left: `${20 + i * 15}%`,
                    top: `${20 + i * 10}%`
                  }}
                  animate={{
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0],
                    y: [0, -20, -40]
                  }}
                  transition={{
                    duration: 3,
                    delay: i * 0.5,
                    repeat: Infinity,
                    ease: "easeOut"
                  }}
                />
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}

export default FloatingAvatar